import { encodeDate } from '@campfire/hot-date';
import { DateTime } from 'luxon';
import { useHistory } from 'react-router';
import { FugFile } from '@campfire/file-upload-gallery';
import { StringParam, useQueryParam } from 'use-query-params';
import { RosterTemplateFormValues } from './RosterTemplateForm';
import { useEndpointFetch } from '../../../../global/network/useEndpointFetch';
import { useSnackbar } from '../../../../global/config/useSnackbar';

interface PlacesAddress {
  description: string;
  formatted: string;
  placesId?: string;
  latitude: number;
  longitude: number;
}

export interface SaveRosterTemplateFetchLocationVariables {
  activityLocationId?: string;
  placesAddress: PlacesAddress;
  timeZone: string;
  comments: string;
}

export interface SaveRosterTemplateFetchVariables {
  rosterTemplateId?: string;
  rosterName?: string | null;
  rosterNotes?: string | null;
  rosterDates: string[];
  location: PlacesAddress | null;
  activityId?: string;
  remoteLocation: {
    activityRemoteLocationId?: string;
    details?: string;
  } | null;
}

export interface SaveRosterTemplateResponse {
  rosterTemplateIds: string[];
  skippedDates: string[];
}

export const useSaveRosterTemplate = () => {
  return useEndpointFetch<SaveRosterTemplateFetchVariables, SaveRosterTemplateResponse>(
    '/vm/activity/roster-template/save',
    {
      formData: true,
    }
  );
};
export const useCancelActivityFetch = () => {
  return useEndpointFetch<
    {
      activityId: string;
      activityDate: string;
      activityCancellationReasonId: string;
      description: string;
      addedAttachments?: FugFile[];
      attachmentCreationTokens?: string[];
    },
    { cancelledActivityId: string }
  >('/vm/activity/cancel', { formData: true });
};
export const useDeleteRosterTemplate = () => {
  return useEndpointFetch<{ rosterTemplateId: string }>('/vm/activity/roster-template/delete');
};

export const useApproveRosterTemplateFetch = () => {
  return useEndpointFetch<{ rosterTemplateId: string }>('/vm/activity/roster-template/approve');
};

export const useRejectRosterTemplateFetch = () => {
  return useEndpointFetch<{ rosterTemplateId: string }>('/vm/activity/roster-template/reject');
};

export const useRosterTemplateApprovalActions = () => {
  const approveRosterTemplate = useApproveRosterTemplateFetch();
  const rejectRosterTemplate = useRejectRosterTemplateFetch();
  const { setSnackbar } = useSnackbar();

  const runApproveRosterTemplate = (rosterTemplateId: string, onSuccess?: () => void) => {
    approveRosterTemplate.run({ rosterTemplateId })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({ open: true, message: 'Unable to approve roster template', variant: 'error' });
          return;
        }
        setSnackbar({ open: true, message: 'Roster template approved', variant: 'success' });
        if (onSuccess) onSuccess();
      })
      .catch(() => setSnackbar({ open: true, message: 'Unable to approve roster template', variant: 'error' }));
  };

  const runRejectRosterTemplate = (rosterTemplateId: string, onSuccess?: () => void) => {
    rejectRosterTemplate.run({ rosterTemplateId })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({ open: true, message: 'Unable to reject roster template', variant: 'error' });
          return;
        }
        setSnackbar({ open: true, message: 'Roster template rejected', variant: 'success' });
        if (onSuccess) onSuccess();
      })
      .catch(() => setSnackbar({ open: true, message: 'Unable to reject roster template', variant: 'error' }));
  };

  return {
    runApproveRosterTemplate,
    runRejectRosterTemplate,
    isApproving: approveRosterTemplate.isLoading,
    isRejecting: rejectRosterTemplate.isLoading,
  };
};

export const useRosterTemplateActions = () => {
  const saveRosterTemplate = useSaveRosterTemplate();
  const deleteRosterTemplate = useDeleteRosterTemplate();
  const cancelActivity = useCancelActivityFetch();
  const history = useHistory();
  const [view] = useQueryParam('view', StringParam);
  const viewParam = view === 'bulk' ? '&view=bulk' : '';
  const { setSnackbar } = useSnackbar();

  const runSaveRosterTemplate = ({ values, onSuccess }: { values: RosterTemplateFormValues; onSuccess?: () => void }) => {
    if (!values.rosterDates || values.rosterDates.length === 0) {
      return Promise.resolve();
    }

    const rosterDates = values.rosterDates.map((date) => encodeDate(DateTime.fromJSDate(date)));
    const firstDate = values.rosterDates[0];

    const handleSaveRosterTemplateSuccess = () => {
      if (onSuccess) {
        onSuccess();
      } else {
        history.push(
          `/management/rosters?activityDate=${encodeDate(DateTime.fromJSDate(firstDate))}&activityId=${
            values.activityId
          }${viewParam}`
        );
      }
    };

    return saveRosterTemplate
      .run({
        rosterTemplateId: values.rosterTemplateId,
        activityId: values.activityId,
        rosterName: values.rosterName,
        rosterNotes: values.rosterNotes,
        rosterDates,
        location: values.location.description ? values.location : null,
        remoteLocation: values.remoteLocation ?? null,
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({
            open: true,
            message: 'Unable to save roster',
            variant: 'error',
          });
          return;
        }

        const createdCount = res.data?.rosterTemplateIds?.length ?? 0;
        const skippedCount = res.data?.skippedDates?.length ?? 0;

        let message = createdCount === 1 ? 'Roster saved' : `${createdCount} rosters saved`;
        if (skippedCount > 0) {
          message += ` (${skippedCount} skipped)`;
        }

        setSnackbar({
          open: true,
          message,
          variant: 'success',
        });
        handleSaveRosterTemplateSuccess();
      })
      .catch(() =>
        setSnackbar({
          open: true,
          message: 'Unable to save roster',
          variant: 'error',
        })
      );
  };

  const runDeleteRosterTemplate = ({ rosterTemplateId }: { rosterTemplateId: string }) => {
    const handleDeleteRosterTemplateSuccess = () => history.push(`/management/rosters`);
    deleteRosterTemplate
      .run({
        rosterTemplateId,
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({
            open: true,
            message: res.data?.message || 'Unable to delete roster',
            variant: 'error',
          });

          return;
        }
        setSnackbar({
          open: true,
          message: 'Roster removed',
          variant: 'success',
        });
        handleDeleteRosterTemplateSuccess();
      })
      .catch(() =>
        setSnackbar({
          open: true,
          message: 'Unable to delete roster',
          variant: 'error',
        })
      );
  };
  const runCancelActivity = ({
    activityCancellationReasonId,
    activityId,
    activityDate,
    description,
    addedAttachments,
    attachmentCreationTokens,
    handleSuccess,
  }: {
    activityCancellationReasonId: string;
    activityId: string;
    activityDate: string;
    description: string;
    addedAttachments?: FugFile[];
    attachmentCreationTokens?: string[];
    handleSuccess?: () => void;
  }) => {
    cancelActivity
      .run({
        activityId,
        activityDate,
        activityCancellationReasonId,
        description,
        addedAttachments,
        attachmentCreationTokens,
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({
            open: true,
            message: 'Unable to cancel activity',
            variant: 'error',
          });
          return;
        }

        setSnackbar({
          open: true,
          message: 'Activity cancelled',
          variant: 'success',
        });
        if (handleSuccess) handleSuccess();
      })
      .catch(() =>
        setSnackbar({
          open: true,
          message: 'Unable to cancel activity',
          variant: 'error',
        })
      );
  };
  return {
    runSaveRosterTemplate,
    runSaveRosterTemplateLoading: saveRosterTemplate.isLoading,
    runDeleteRosterTemplate,
    runDeleteRosterTemplateLoading: deleteRosterTemplate.isLoading,
    runCancelActivity,
  };
};
